/**
 * Google / Apple sign-in via Firebase Auth OAuth providers.
 *
 * Popup-first on all devices (popup doesn't depend on third-party storage). Redirect
 * is only a safety net; completeOAuthRedirectIfNeeded() finalizes it on next load.
 * authDomain is set to the app's custom domain in firebase.js so both flows work on
 * browsers that block third-party storage (Safari/iOS, Firefox, Chrome 115+).
 */
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth';

/** Popup errors that are user-driven and should be silently ignored by callers. */
export function isIgnorableOAuthError(error) {
  const code = error?.code;
  return (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    code === 'auth/user-cancelled'
  );
}

/** Human-readable message for OAuth errors worth surfacing; null if it should be ignored. */
export function oauthErrorMessage(error) {
  if (isIgnorableOAuthError(error)) return null;
  switch (error?.code) {
    case 'auth/account-exists-with-different-credential':
      return 'This email is registered with a password. Sign in with email, or reset your password.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Please allow popups and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return 'Could not complete sign-in. Please try again.';
  }
}

function googleProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

function appleProvider() {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  return provider;
}

/**
 * Sign in with Google (popup). On popup-blocked environments, falls back to redirect
 * (resolved later by completeOAuthRedirectIfNeeded). Returns the UserCredential, or
 * null when a redirect was started.
 */
export async function signInWithGoogle(auth) {
  try {
    return await signInWithPopup(auth, googleProvider());
  } catch (error) {
    if (error?.code === 'auth/popup-blocked') {
      await signInWithRedirect(auth, googleProvider());
      return null;
    }
    throw error;
  }
}

/** Sign in with Apple (popup; redirect fallback on popup-blocked). */
export async function signInWithApple(auth) {
  try {
    return await signInWithPopup(auth, appleProvider());
  } catch (error) {
    if (error?.code === 'auth/popup-blocked') {
      await signInWithRedirect(auth, appleProvider());
      return null;
    }
    throw error;
  }
}

/**
 * Resolve a pending redirect sign-in (no-op when there isn't one). Returns the
 * UserCredential when a redirect just completed, otherwise null.
 */
export async function completeOAuthRedirectIfNeeded(auth) {
  try {
    return await getRedirectResult(auth);
  } catch (error) {
    console.error('completeOAuthRedirectIfNeeded:', error);
    return null;
  }
}
